import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { interfaceOf, type RoleName } from '@/shared/lib/roles';

import type { Permission, SessionOut, UserOut } from './types';

/**
 * The signed-in session. The access token lives only here, in memory: it is never
 * persisted, so a page script reading storage cannot steal it. The refresh token
 * is an httpOnly cookie the browser manages.
 */
export interface SessionState {
  /** `unknown` until the first refresh attempt on startup has finished. */
  status: 'unknown' | 'authenticated' | 'anonymous';
  accessToken: string | null;
  user: UserOut | null;
}

const initialState: SessionState = { status: 'unknown', accessToken: null, user: null };

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    sessionStarted(state, action: PayloadAction<SessionOut>) {
      state.status = 'authenticated';
      state.accessToken = action.payload.accessToken;
      state.user = action.payload.user;
    },
    sessionEnded(state) {
      state.status = 'anonymous';
      state.accessToken = null;
      state.user = null;
    },
    userUpdated(state, action: PayloadAction<UserOut>) {
      state.user = action.payload;
    },
  },
});

export const { sessionStarted, sessionEnded, userUpdated } = sessionSlice.actions;
export const sessionReducer = sessionSlice.reducer;

export interface WithSession {
  session: SessionState;
}

export const selectSessionStatus = (state: WithSession) => state.session.status;
export const selectAccessToken = (state: WithSession) => state.session.accessToken;
export const selectUser = (state: WithSession) => state.session.user;

/**
 * A guest: public lessons tried without an account. It has a session (what it does
 * is kept, and moves into the account it signs in to) but no account: it is shown
 * what a visitor is.
 */
export const selectIsGuest = (state: WithSession) => state.session.user?.role === 'guest';

/** Signed in to an account: a session that is not a guest's. */
export const selectSignedIn = (state: WithSession) =>
  state.session.status === 'authenticated' && !selectIsGuest(state);

/**
 * What the signed-in account may do; nothing for a visitor or a guest. These only
 * decide what the interface offers; the server checks every request against the
 * account as it is at that moment.
 */
export const selectRole = (state: WithSession): RoleName | undefined => interfaceOf(state.session.user?.role);

/** Whether the account's one role is this one, or one of these. */
export const selectHasRole =
  (...roles: RoleName[]) =>
  (state: WithSession) => {
    const role = selectRole(state);
    return role !== undefined && roles.includes(role);
  };

/** An attribute of an admin, granted by root. Root holds every one of them. */
export const selectCan = (permission: Permission) => (state: WithSession) =>
  state.session.user?.permissions.includes(permission) ?? false;

